import { getCategoryLabel, getCategoryList } from "@/lib/categories";
import { calculateDairyComposition } from "@/lib/ingredients/form-estimators";
import { IngredientCategory } from "@/lib/types";
import { useReducer } from "react";
import { CreateIngredientFormData } from "./forms/schema";

export type FormMode = "simple" | "advanced" | "nutrition";

const CATEGORY_MODES = {
  dairy: ["simple", "nutrition", "advanced"],
  other: ["nutrition", "advanced"],
  alcohol: ["simple", "advanced"],
  sweetener: ["simple", "advanced"],
  chocolate: ["simple", "nutrition", "advanced"],
  emulsifier: ["simple", "advanced"],
  stabilizer: ["simple", "advanced"],
  fruit: ["nutrition", "advanced"],
  nut: ["advanced"],
  egg: ["advanced"],
} satisfies Record<IngredientCategory, FormMode[]>;

export const CATEGORY_DEFAULT_FIELDS = [
  "fatPct",
  "sugarsPct",
  "lactosePct",
  "waterPct",
  "proteinPct",
  "carbPct",
  "fiberPct",
  "alcoholPct",
  "energyKcal",
  "pod",
  "pac",
  "cocoaPct",
  "cocoaSolidsNonfatPct",
  "cocoaFatPct",
  "emulsifierPct",
  "stabilizerPct",
  "saturatedFatPct",
  "transFatPct",
] as const satisfies ReadonlyArray<keyof CreateIngredientFormData>;

export const categories: {
  value: IngredientCategory;
  label: string;
  modes: FormMode[];
}[] = getCategoryList().map((category) => ({
  value: category,
  label: getCategoryLabel(category),
  modes: CATEGORY_MODES[category] ?? ["advanced"],
}));

export function getCategoryDefaults(
  category: IngredientCategory,
): Partial<CreateIngredientFormData> {
  const defaults: Partial<CreateIngredientFormData> = {
    fatPct: 0,
    sugarsPct: 0,
    lactosePct: 0,
    waterPct: 0,
    proteinPct: 0,
    carbPct: 0,
    fiberPct: 0,
    alcoholPct: 0,
    energyKcal: 0,
    pod: 0,
    pac: 0,
    cocoaPct: 0,
    cocoaSolidsNonfatPct: 0,
    cocoaFatPct: 0,
    emulsifierPct: 0,
    stabilizerPct: 0,
    saturatedFatPct: 0,
    transFatPct: 0,
  };

  switch (category) {
    case "dairy":
      return {
        ...defaults,
        ...calculateDairyComposition(3.6),
      };
    case "sweetener":
      return {
        ...defaults,
        sugarsPct: 100,
        energyKcal: 4.0,
        pod: 100,
        pac: 100,
        carbPct: 100,
      };
    case "fruit":
      return {
        ...defaults,
        waterPct: 85,
        sugarsPct: 15,
        energyKcal: 0.6,
        pod: 12,
        pac: 12,
      };
    case "chocolate":
      return {
        ...defaults,
        waterPct: 3,
        fatPct: 13,
        sugarsPct: 2,
        cocoaPct: 100,
        cocoaSolidsNonfatPct: 82,
        cocoaFatPct: 13,
        energyKcal: 2.2,
        pod: 2,
        pac: 2,
      };
    case "nut":
      return {
        ...defaults,
        waterPct: 1,
        fatPct: 50,
        sugarsPct: 0,
        pod: 0,
        pac: 3,
        energyKcal: 5.8,
      };
    case "alcohol":
      return {
        ...defaults,
        waterPct: 60,
        alcoholPct: 40,
        energyKcal: 2.3,
        pod: 0,
        pac: 247,
      };
    case "emulsifier":
      return {
        ...defaults,
        emulsifierPct: 100,
      };
    case "stabilizer":
      return {
        ...defaults,
        stabilizerPct: 100,
      };
    default:
      return defaults;
  }
}

interface CreateIngredientContext {
  selectedCategory: IngredientCategory | null;
  formMode: FormMode | null;
  availableModes: FormMode[];
}

type CreateIngredientEvents =
  | { type: "SELECT_CATEGORY"; category: IngredientCategory }
  | { type: "SELECT_MODE"; mode: FormMode }
  | { type: "RESET" };

const INITIAL_CONTEXT: CreateIngredientContext = {
  selectedCategory: null,
  formMode: null,
  availableModes: [],
};

function createIngredientReducer(
  context: CreateIngredientContext,
  event: CreateIngredientEvents,
): CreateIngredientContext {
  switch (event.type) {
    case "SELECT_CATEGORY": {
      const category = categories.find(
        (candidate) => candidate.value === event.category,
      );
      return {
        selectedCategory: event.category,
        formMode: category?.modes[0] ?? null,
        availableModes: category?.modes ?? [],
      };
    }
    case "SELECT_MODE":
      return { ...context, formMode: event.mode };
    case "RESET":
      return INITIAL_CONTEXT;
  }
}

export function useCreateIngredientForm() {
  const [context, send] = useReducer(createIngredientReducer, INITIAL_CONTEXT);

  const selectCategory = (category: IngredientCategory) => {
    send({ type: "SELECT_CATEGORY", category });
  };

  const selectMode = (mode: FormMode) => {
    send({ type: "SELECT_MODE", mode });
  };

  const reset = () => {
    send({ type: "RESET" });
  };

  return {
    selectedCategory: context.selectedCategory,
    formMode: context.formMode,
    availableModes: context.availableModes,
    selectCategory,
    selectMode,
    reset,
  };
}
